import React, { useState, useEffect } from 'react'
import { FileText } from 'lucide-react'

const HistoryAndGatepass = () => {
  const [gatePasses, setGatePasses] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [filterStatus, setFilterStatus] = useState('all') // all, issued, pending, expired
  const [searchTerm, setSearchTerm] = useState('')

  // Mock data for now - replace with actual API call
  useEffect(() => {
    // Simulate API call
    setTimeout(() => {
      setGatePasses([]) // Empty for now
      setLoading(false)
    }, 1000)
  }, [])

  const filteredGatePasses = gatePasses.filter((gatePass) => {
    // Filter by status
    if (filterStatus !== 'all') {
      const status = gatePass.status?.toLowerCase() || gatePass.gatePassStatus?.toLowerCase()
      if (status !== filterStatus) {
        return false
      }
    }

    // Filter by search term (lot number, vehicle name, or gate pass number)
    if (searchTerm && searchTerm.trim() !== '') {
      const searchLower = searchTerm.toLowerCase().trim()
      const lotNumber = gatePass?.vehicle?.lotNumber || gatePass?.lotNumber || ''
      const vehicleName = gatePass?.vehicle?.name || gatePass?.vehicleName || ''
      const gatePassNumber = gatePass?.gatePassNumber || gatePass?.id?.toString() || ''

      return (
        lotNumber.toLowerCase().includes(searchLower) ||
        vehicleName.toLowerCase().includes(searchLower) ||
        gatePassNumber.toLowerCase().includes(searchLower)
      )
    }

    return true
  })

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <div className="text-red-500 mb-4">Error: {error}</div>
        <button
          onClick={() => window.location.reload()}
          className="bg-orange-500 text-white px-4 py-2 rounded hover:bg-orange-600"
        >
          Retry
        </button>
      </div>
    )
  }

  return (
    <div className="space-y-2">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-800">
          History & Gate Pass
        </h1>
        <div className="text-sm font-bold text-black">
          Total Gate Passes: {gatePasses.length}
        </div>
      </div>

      {/* Filter Navigation */}
      <div className="border-b border-gray-200">
        {/* Filter Tabs and Search */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 py-4">
          {/* Filter Tabs */}
          <nav className="flex space-x-1 sm:space-x-2 overflow-x-auto pb-2 lg:pb-0 scrollbar-hide">
            {[
              { key: 'all', label: 'All' },
              { key: 'issued', label: 'Issued' },
              { key: 'pending', label: 'Pending' },
              { key: 'active', label: 'Active' },
              { key: 'expired', label: 'Expired' },
              { key: 'used', label: 'Used' },
            ].map((filter) => (
              <button
                key={filter.key}
                onClick={() => setFilterStatus(filter.key)}
                className={`px-2 sm:px-3 py-2 rounded-full font-medium text-xs transition-all duration-200 whitespace-nowrap ${
                  filterStatus === filter.key
                    ? 'bg-gradient-to-r from-[#FF4A33] to-[#FFB74D] text-white shadow-md'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200 hover:text-gray-800'
                }`}
              >
                <span className="hidden sm:inline">{filter.label}</span>
                <span className="sm:hidden">{filter.label.charAt(0)}</span>
                <span className="ml-1">
                  (
                  {
                    gatePasses.filter((gp) => {
                      const status = gp.status?.toLowerCase() || gp.gatePassStatus?.toLowerCase()
                      return filter.key === 'all' || status === filter.key
                    }).length
                  }
                  )
                </span>
              </button>
            ))}
          </nav>

          {/* Search Field */}
          <div className="flex items-center w-full lg:w-auto">
            <div className="relative flex-1 lg:flex-none">
              <input
                type="text"
                placeholder="Search by Lot Number, Vehicle Name, or Gate Pass Number"
                className="w-full sm:w-80 px-4 py-2 pl-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-400 text-sm"
                value={searchTerm || ''}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <svg
                className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-orange-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </div>
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="ml-2 p-2 text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0"
                title="Clear search"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Gate Passes List */}
      <div className="space-y-3">
        {filteredGatePasses.length === 0 ? (
          <div className="text-center py-12">
            <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              No gate passes found
            </h3>
            <p className="text-gray-500">
              {filterStatus === 'all'
                ? "You don't have any gate passes yet."
                : `No ${filterStatus} gate passes found.`}
            </p>
          </div>
        ) : (
          <div>

            <p>Gate passes</p>
          </div>
        )}
      </div>
    </div>
  )
}

export default HistoryAndGatepass
